import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Eye, Ban, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Loader, fmt, StatusBadge, fmtDate, EmptyState } from "@/components/shared";
import { AdminPageHeader } from "./adminShared";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const nav = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get("/admin/users", { params: search ? { search } : {} });
    setUsers(r.data);
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/users/${id}/status`, { status }); toast.success(`User ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-users">
      <AdminPageHeader eyebrow="Management" title="Users"
        right={
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..."
              className="bg-slate-900/60 border border-cyan-500/20 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-400 w-full sm:w-64" />
          </div>
        } />
      {users === null ? <Loader /> : users.length ? (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-left text-slate-500 border-b border-white/5">
              {["User ID", "Name", "Email", "Mobile", "Joined", "Status", "Deposit", "Balance", "Refs", "Actions"].map((h) => <th key={h} className="pb-3 px-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 last:border-0" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-2 font-mono text-xs text-slate-500">{u.id.slice(0, 8)}</td>
                  <td className="py-3 px-2 text-slate-200">{u.full_name}</td>
                  <td className="py-3 px-2 text-slate-400">{u.email}</td>
                  <td className="py-3 px-2 text-slate-400">{u.mobile}</td>
                  <td className="py-3 px-2 text-slate-500">{fmtDate(u.created_at)}</td>
                  <td className="py-3 px-2"><StatusBadge status={u.account_status} /></td>
                  <td className="py-3 px-2 font-mono text-slate-300">{fmt(u.total_deposit)}</td>
                  <td className="py-3 px-2 font-mono text-cyan-300">{fmt(u.available_balance)}</td>
                  <td className="py-3 px-2 text-slate-300">{u.referral_count}</td>
                  <td className="py-3 px-2">
                    <div className="flex gap-2">
                      <button onClick={() => nav(`/admin/users/${u.id}`)} data-testid={`user-view-${u.id}`} className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20"><Eye className="w-4 h-4" /></button>
                      {u.account_status === "active" ? (
                        <button onClick={() => setStatus(u.id, "suspended")} data-testid={`user-suspend-${u.id}`} className="p-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20"><Ban className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => setStatus(u.id, "active")} data-testid={`user-activate-${u.id}`} className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"><CheckCircle2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState title="No users found" />}
    </div>
  );
}
